'use client';

import type { ButtonGroupProps } from '@magicschool/spellbook/components/ButtonGroup';
import { ButtonGroup, buttonGroupVariants } from '@magicschool/spellbook/components/ButtonGroup';
import { CollapsibleButtonGroupButtonSlot } from '@magicschool/spellbook/components/ButtonGroup/slots';
import type { CollapsedAction } from '@magicschool/spellbook/components/ButtonGroup/utils';
import { getCollapsedAction } from '@magicschool/spellbook/components/ButtonGroup/utils';
import { collapsedActionContent, isContainerBreakpoint } from '@magicschool/spellbook/components/CollapsibleButtonGroup/utils';
import type { DropdownMenuContentProps, DropdownMenuProps } from '@magicschool/spellbook/components/DropdownMenu';
import { DropdownMenu } from '@magicschool/spellbook/components/DropdownMenu';
import type { IconButtonProps } from '@magicschool/spellbook/components/IconButton';
import { IconButton } from '@magicschool/spellbook/components/IconButton';
import type { BreakpointKey } from '@magicschool/spellbook/hooks/useBreakpoint';
import { useOrderedSlots } from '@magicschool/spellbook/hooks/useOrderedSlots';
import { mapOrderedSlots } from '@magicschool/spellbook/lib/slots';
import { cn } from '@magicschool/spellbook/lib/utils';
import { cva } from 'class-variance-authority';
import type { ReactNode } from 'react';

/**
 * A viewport breakpoint (e.g. `'md'`), or the same key prefixed with `@` for a CSS container-query
 * breakpoint measuring the group's own width instead (e.g. `'@md'`). Both are pure CSS — no
 * `ResizeObserver`, no media-query hook, no re-render. The `@` keys query this group's own named
 * `@container/button-group`, but no `--container-*` theme tokens are defined in this repo, so
 * they resolve against Tailwind's default container scale, not the `--breakpoint-*` scale —
 * `'md'` and `'@md'` do NOT collapse at the same width today.
 */
export type CollapsedBelow = BreakpointKey | `@${BreakpointKey}`;

export type CollapsibleButtonGroupProps = ButtonGroupProps & {
  /**
   * Renders a single trigger + `DropdownMenu` instead of the row. Omit to let `collapsedBelow` decide;
   * set explicitly to take over completely (e.g. from a setting, or your own measurement).
   */
  collapsed?: boolean;
  /**
   * Breakpoint below which the group auto-collapses, purely via CSS. A plain key (`'md'`) uses a
   * standard viewport media-query breakpoint; an `@`-prefixed key (`'@md'`) uses a container query on
   * the group's own width instead. Only consulted when `collapsed` is omitted.
   */
  collapsedBelow?: CollapsedBelow;
  /**
   * Props for the collapsed trigger `IconButton`. Defaults to `{ name: 'more-horizontal', variant: 'ghost',
   * color: 'neutral', label: 'More actions' }` — override `label` with something more specific to what
   * this particular group's actions are (as accessible text) whenever it can render collapsed.
   */
  collapsedTriggerProps?: Partial<IconButtonProps>;
  /** Visible tooltip content for the collapsed trigger. Omit for no tooltip (the trigger's `label` remains its accessible name either way). */
  collapsedTriggerTooltip?: ReactNode;
  /** Any `DropdownMenu` prop for the collapsed menu (`open`, `onOpenChange`, `modal`, `side`, ...). */
  collapsedMenuProps?: Partial<Omit<DropdownMenuProps, 'children'>>;
  /** Any `DropdownMenu.Content` prop for the collapsed menu (`align`, `sideOffset`, ...). Defaults to `align="start"`. */
  collapsedContentProps?: Partial<Omit<DropdownMenuContentProps, 'children'>>;
};

/**
 * Visibility of the row vs. the collapsed trigger for `collapsedBelow`, one pair per CSS mechanism.
 * Tailwind's content scanner only picks up class names it can see as complete literal text in source,
 * so every `breakpoint:flex` / `breakpoint:hidden` combination is spelled out in full here rather than
 * built from a template literal.
 */
const viewportRowVisibilityVariants = cva('hidden', {
  variants: {
    breakpoint: {
      xs: 'xs:flex',
      sm: 'sm:flex',
      md: 'md:flex',
      lg: 'lg:flex',
      xl: 'xl:flex',
    } satisfies Record<BreakpointKey, string>,
  },
});

const viewportTriggerVisibilityVariants = cva('flex', {
  variants: {
    breakpoint: {
      xs: 'xs:hidden',
      sm: 'sm:hidden',
      md: 'md:hidden',
      lg: 'lg:hidden',
      xl: 'xl:hidden',
    } satisfies Record<BreakpointKey, string>,
  },
});

const containerRowVisibilityVariants = cva('hidden', {
  variants: {
    breakpoint: {
      xs: '@xs/button-group:flex',
      sm: '@sm/button-group:flex',
      md: '@md/button-group:flex',
      lg: '@lg/button-group:flex',
      xl: '@xl/button-group:flex',
    } satisfies Record<BreakpointKey, string>,
  },
});

const containerTriggerVisibilityVariants = cva('flex', {
  variants: {
    breakpoint: {
      xs: '@xs/button-group:hidden',
      sm: '@sm/button-group:hidden',
      md: '@md/button-group:hidden',
      lg: '@lg/button-group:hidden',
      xl: '@xl/button-group:hidden',
    } satisfies Record<BreakpointKey, string>,
  },
});

/** The collapsed trigger + menu, built from each slot's `getCollapsedAction`. */
function CollapsedMenu({
  orderedSlots,
  collapsedTriggerProps,
  collapsedTriggerTooltip,
  collapsedMenuProps,
  collapsedContentProps,
}: {
  orderedSlots: ReturnType<typeof useOrderedSlots>['orderedSlots'];
  collapsedTriggerProps?: Partial<IconButtonProps>;
  collapsedTriggerTooltip?: ReactNode;
  collapsedMenuProps?: Partial<Omit<DropdownMenuProps, 'children'>>;
  collapsedContentProps?: Partial<Omit<DropdownMenuContentProps, 'children'>>;
}) {
  const trigger = <IconButton name="more-horizontal" variant="ghost" color="neutral" label="More actions" {...collapsedTriggerProps} />;

  return (
    <DropdownMenu {...collapsedMenuProps}>
      {collapsedTriggerTooltip ? (
        <DropdownMenu.Tooltip mode="label">
          <DropdownMenu.Trigger>{trigger}</DropdownMenu.Trigger>
          <DropdownMenu.TooltipContent>{collapsedTriggerTooltip}</DropdownMenu.TooltipContent>
        </DropdownMenu.Tooltip>
      ) : (
        <DropdownMenu.Trigger>{trigger}</DropdownMenu.Trigger>
      )}
      <DropdownMenu.Content align="start" {...collapsedContentProps}>
        {orderedSlots
          .map((slot) => ({ slot, action: getCollapsedAction(slot.component.props as Record<string, unknown>) }))
          .filter((entry): entry is { slot: (typeof orderedSlots)[number]; action: CollapsedAction } => entry.action !== null)
          .sort((a, b) => (a.action.order ?? a.slot.originalIndex) - (b.action.order ?? b.slot.originalIndex))
          .map(({ slot, action }) => {
            if (action.subItems) {
              return (
                <DropdownMenu.Sub key={slot.originalIndex} {...action.submenuProps}>
                  <DropdownMenu.SubTrigger>{collapsedActionContent(action.icon, action.label)}</DropdownMenu.SubTrigger>
                  <DropdownMenu.SubContent>
                    {action.subItems.map((subItem) => (
                      <DropdownMenu.Item key={subItem.label} onSelect={subItem.onSelect} disabled={subItem.disabled}>
                        {collapsedActionContent(subItem.icon, subItem.label)}
                      </DropdownMenu.Item>
                    ))}
                  </DropdownMenu.SubContent>
                </DropdownMenu.Sub>
              );
            }

            return (
              <DropdownMenu.Item key={slot.originalIndex} onSelect={action.onSelect} disabled={action.disabled}>
                {collapsedActionContent(action.icon, action.label)}
              </DropdownMenu.Item>
            );
          })}
      </DropdownMenu.Content>
    </DropdownMenu>
  );
}

const CollapsibleButtonGroupBase: React.FC<CollapsibleButtonGroupProps> = ({
  children,
  className,
  orientation = 'horizontal',
  display = 'inline',
  collapsed,
  collapsedBelow,
  collapsedTriggerProps,
  collapsedTriggerTooltip,
  collapsedMenuProps,
  collapsedContentProps,
  ...props
}) => {
  const { orderedSlots } = useOrderedSlots(children, {
    buttons: { Component: CollapsibleButtonGroupButtonSlot, props: { orientation, display } },
  });

  const collapsedMenu = (
    <CollapsedMenu
      orderedSlots={orderedSlots}
      collapsedTriggerProps={collapsedTriggerProps}
      collapsedTriggerTooltip={collapsedTriggerTooltip}
      collapsedMenuProps={collapsedMenuProps}
      collapsedContentProps={collapsedContentProps}
    />
  );

  // Auto-collapsing (via either CSS mechanism) needs both representations rendered at once, toggled
  // purely by CSS — there's no JS boolean to branch on the way there is for the `collapsed`-controlled case.
  if (collapsed === undefined && collapsedBelow) {
    const isContainer = isContainerBreakpoint(collapsedBelow);
    const breakpointKey = (isContainer ? collapsedBelow.slice(1) : collapsedBelow) as BreakpointKey;
    const rowVisibilityVariants = isContainer ? containerRowVisibilityVariants : viewportRowVisibilityVariants;
    const triggerVisibilityVariants = isContainer ? containerTriggerVisibilityVariants : viewportTriggerVisibilityVariants;

    return (
      // biome-ignore lint/a11y/useSemanticElements: <fieldset> is for form controls, not action button groups. role="group" is the correct WAI-ARIA pattern for grouping related interactive elements (see https://www.w3.org/WAI/ARIA/apg/patterns/toolbar/)
      <div
        role="group"
        data-spellbook-name="collapsible-button-group"
        className={cn(isContainer && '@container/button-group w-full', className)}
        {...props}
      >
        <div
          data-spellbook-name="button-group-row"
          className={cn(buttonGroupVariants({ orientation, display }), rowVisibilityVariants({ breakpoint: breakpointKey }))}
        >
          {mapOrderedSlots(orderedSlots)}
        </div>
        <div data-spellbook-name="button-group-collapsed" className={triggerVisibilityVariants({ breakpoint: breakpointKey })}>
          {collapsedMenu}
        </div>
      </div>
    );
  }

  const isCollapsed = collapsed ?? false;

  if (isCollapsed) {
    return (
      // biome-ignore lint/a11y/useSemanticElements: <fieldset> is for form controls, not action button groups. role="group" is the correct WAI-ARIA pattern for grouping related interactive elements (see https://www.w3.org/WAI/ARIA/apg/patterns/toolbar/)
      <div role="group" data-spellbook-name="collapsible-button-group" className={className} {...props}>
        {collapsedMenu}
      </div>
    );
  }

  return (
    <ButtonGroup
      data-spellbook-name="collapsible-button-group"
      orientation={orientation}
      display={display}
      className={className}
      {...props}
    >
      {children}
    </ButtonGroup>
  );
};

/**
 * `ButtonGroup`, plus the ability to collapse into a single trigger + `DropdownMenu` on limited space. A
 * separate component from `ButtonGroup` so plain `ButtonGroup` usages are never affected by this behavior.
 *
 * ### When to use
 * - Everything `ButtonGroup` is for, when the group also needs to adapt to a narrow viewport or container
 *
 * ### When NOT to use
 * - When the group should never collapse: use `ButtonGroup` directly
 * - For several groups in one row, where which ones fit depends on the others: use `Toolbar`, which measures the row instead of collapsing at a breakpoint, and keeps each collapsed control's own behavior rather than rebuilding it as a menu item
 * - See `ButtonGroup`'s own "When NOT to use" for everything else
 *
 * ### API
 * Everything from `ButtonGroup` (`orientation`, `display`) — note `display` defaults to `'inline'` here rather
 * than `ButtonGroup`'s `'connected'`, since a collapsing group is usually a looser cluster of actions rather
 * than a tightly fused split-button — plus:
 * - `collapsed`: Renders a single trigger + `DropdownMenu` instead of the row when `true`. Omit to let `collapsedBelow` decide; set explicitly to take over completely.
 * - `collapsedBelow`: Breakpoint below which the group auto-collapses, purely via CSS. A plain key (`'md'`) uses a standard viewport media-query breakpoint; an `@`-prefixed key (`'@md'`) uses a container query scoped to this group's own named `@container/button-group` instead. Only consulted when `collapsed` is omitted.
 * - `collapsedTriggerProps`: Props for the collapsed trigger `IconButton`. Defaults to `{ name: 'more-horizontal', variant: 'ghost', color: 'neutral', label: 'More actions' }` — override `label` with something more specific to this group's actions.
 * - `collapsedTriggerTooltip`: Visible tooltip content for the collapsed trigger. Omit for no tooltip.
 * - `collapsedMenuProps`: Any `DropdownMenu` prop for the collapsed menu (`open`, `onOpenChange`, `modal`, `side`, ...).
 * - `collapsedContentProps`: Any `DropdownMenu.Content` prop for the collapsed menu (`align`, `sideOffset`, ...). Defaults to `align="start"`.
 *
 * ### Composition (slots)
 * - `<CollapsibleButtonGroup.Button>`: Same slot as `ButtonGroup.Button` — wraps `Button`, `IconButton`, or interactive wrappers like `Tooltip`/`DropdownMenu`.
 *   - `collapsedLabel` / `collapsedIcon`: Override the label/icon used for this action's collapsed menu item. Falls back to a plain child `IconButton`'s own `label`/`name`; required for a plain `Button` child (its text isn't reliably extractable) or when overriding the default.
 *   - `collapsedItems`: Required when this slot's child is itself a `DropdownMenu` — its live menu content isn't introspected, so the collapsed submenu's items must be provided explicitly.
 *   - `collapsedSubmenuProps`: Any `DropdownMenu.Sub` prop for that collapsed submenu (`onOpenChange`, ...).
 *   - `collapsedOrder`: Position of this action in the collapsed menu, lowest first. Omit to keep its row (JSX) position — only needed when the collapsed order should differ from the row's, e.g. to keep a focus-order-safe row layout while still surfacing an action first once collapsed.
 *
 * ### Visual behavior
 * - `collapsed` renders exactly one representation at a time (row, or trigger + menu) — a JS boolean, single render
 * - `collapsedBelow` (either mechanism) renders both representations at once, toggled purely by CSS media/container queries — no layout measurement, no re-render
 *
 * ### Accessibility
 * - Renders with `role="group"` to communicate related actions to assistive technology
 * - The row and the collapsed trigger + menu expose the same accessible name per action, whichever is currently rendered; under `collapsedBelow`, the inactive representation is `hidden` (removed from the accessibility tree and tab order)
 *
 * ### Additional notes
 * - `collapsedBelow="@..."` places the group somewhere that filling available width makes sense (a toolbar row, a flex sibling with `flex-1`), not inline within running text — it becomes `w-full` (and gains its own named `@container/button-group`) because a size container query needs its width to come from an ancestor, not its own content
 */
export const CollapsibleButtonGroup = Object.assign(CollapsibleButtonGroupBase, {
  Button: CollapsibleButtonGroupButtonSlot,
});
